import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaBell, FaEnvelope, FaSave, FaPaperPlane, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { getNotificationSettings, saveNotificationSettings } from '../lib/emailService';
import usePageTitle from '../hooks/usePageTitle';

const NotificationSettings = () => {
  usePageTitle('Pengaturan Notifikasi');

  const [adminEmails, setAdminEmails] = useState('');
  const [notifyNewRequest, setNotifyNewRequest] = useState(true);
  const [notifyStatusChange, setNotifyStatusChange] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: '' }

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const settings = await getNotificationSettings();
    if (settings) {
      setAdminEmails(settings.admin_emails || '');
      setNotifyNewRequest(settings.notify_new_request ?? true);
      setNotifyStatusChange(settings.notify_status_change ?? true);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    // Validate emails
    if (adminEmails.trim()) {
      const emails = adminEmails.split(',').map(e => e.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalid = emails.find(e => e && !emailRegex.test(e));
      if (invalid) {
        setMessage({ type: 'error', text: `Format email tidak valid: ${invalid}` });
        setSaving(false);
        return;
      }
    }

    try {
      await saveNotificationSettings({
        admin_emails: adminEmails.trim(),
        notify_new_request: notifyNewRequest,
        notify_status_change: notifyStatusChange,
      });
      setMessage({ type: 'success', text: 'Pengaturan berhasil disimpan!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal menyimpan: ' + (error.message || 'Silakan coba lagi.') });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!adminEmails.trim()) {
      setMessage({ type: 'error', text: 'Masukkan email admin terlebih dahulu.' });
      return;
    }

    setTesting(true);
    setMessage(null);

    try {
      const emails = adminEmails.split(',').map(e => e.trim()).filter(Boolean);
      const response = await fetch('/api/send-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emails[0], // Test to first email only
          subject: '[PIMNAS Alumni] Test Notifikasi Email ✅',
          html: `
            <div style="font-family: 'Segoe UI', sans-serif; max-width: 500px; margin: 0 auto; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
              <div style="background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%); padding: 28px 24px; text-align: center;">
                <h1 style="color: #fff; margin: 0; font-size: 20px;">✅ Test Email Berhasil!</h1>
              </div>
              <div style="padding: 24px;">
                <p style="color: #334155; font-size: 14px; line-height: 1.6;">
                  Jika kamu menerima email ini, berarti konfigurasi notifikasi email <strong>PIMNAS Alumni Network</strong> sudah berjalan dengan benar.
                </p>
                <p style="color: #94a3b8; font-size: 12px; margin-top: 16px;">
                  Dikirim pada: ${new Date().toLocaleString('id-ID')}
                </p>
              </div>
            </div>
          `,
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Email test berhasil dikirim ke ${emails[0]}` });
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: `Gagal mengirim test email: ${err.error || 'Unknown error'}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal mengirim test email. Pastikan API sudah dikonfigurasi.' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="font-inter bg-gray-50 dark:bg-dark-900 min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="font-inter bg-gray-50 dark:bg-dark-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 dark:from-dark-950 dark:to-dark-900 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/admin/requests" className="text-gray-300 hover:text-white text-sm inline-flex items-center gap-1 mb-4 transition-colors">
            <FaArrowLeft /> Kembali ke Pengajuan
          </Link>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FaBell className="text-gold-400" /> Pengaturan Notifikasi
          </h1>
          <p className="text-gray-300 mt-2">Konfigurasi notifikasi email untuk pengajuan alumni</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-12">
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-amber-500 to-primary-500"></div>

          <div className="p-6 md:p-8 space-y-8">
            {/* Status Message */}
            {message && (
              <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
              }`}>
                {message.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
                {message.text}
              </div>
            )}

            {/* Admin Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
                <FaEnvelope className="text-primary-500" /> Email Admin Penerima Notifikasi
              </label>
              <input
                type="text"
                value={adminEmails}
                onChange={e => setAdminEmails(e.target.value)}
                placeholder="admin1@email.com, admin2@email.com"
                className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
              />
              <p className="text-xs text-gray-400 mt-1.5">Pisahkan beberapa email dengan koma. Email ini akan menerima notifikasi saat ada pengajuan alumni baru.</p>
            </div>

            {/* Toggle Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Jenis Notifikasi</h3>

              {/* Notify on new request */}
              <label className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-700 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-600 transition">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                  <span className="text-lg">📩</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Pengajuan Baru</p>
                  <p className="text-xs text-gray-400">Kirim email ke admin saat ada pengajuan alumni baru</p>
                </div>
                <div className="relative shrink-0">
                  <input
                    type="checkbox"
                    checked={notifyNewRequest}
                    onChange={e => setNotifyNewRequest(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className={`w-12 h-7 rounded-full transition-colors duration-200 ${notifyNewRequest ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-500'}`}>
                    <div className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200"
                      style={{ transform: `translateX(${notifyNewRequest ? '20px' : '0px'})` }}
                    ></div>
                  </div>
                </div>
              </label>

              {/* Notify on status change */}
              <label className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-700 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-600 transition">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                  <span className="text-lg">📬</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Perubahan Status</p>
                  <p className="text-xs text-gray-400">Kirim email ke pemohon saat pengajuan disetujui/ditolak</p>
                </div>
                <div className="relative shrink-0">
                  <input
                    type="checkbox"
                    checked={notifyStatusChange}
                    onChange={e => setNotifyStatusChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className={`w-12 h-7 rounded-full transition-colors duration-200 ${notifyStatusChange ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-500'}`}>
                    <div className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200"
                      style={{ transform: `translateX(${notifyStatusChange ? '20px' : '0px'})` }}
                    ></div>
                  </div>
                </div>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-dark-700">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-xl transition"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <FaSave />
                )}
                {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
              </button>

              <button
                onClick={handleTestEmail}
                disabled={testing || !adminEmails.trim()}
                className="inline-flex items-center justify-center gap-2 border border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 font-medium py-3 px-6 rounded-xl transition"
              >
                {testing ? (
                  <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin"></div>
                ) : (
                  <FaPaperPlane />
                )}
                {testing ? 'Mengirim...' : 'Kirim Test Email'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
